import { useState, useEffect, useRef, useCallback } from "react";
import { Document, Page as PDFPage, pdfjs } from "react-pdf";
import { LucideFilePlus, Maximize2, Minimize2 } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url,
).toString();

function ExpansionButton({ src, isExpanded, onToggle }) {
  if (!src) return null;
  return (
    <div
      className="absolute inset-0 z-[60] flex items-center justify-center pointer-events-none group"
    >
      <button
        onClick={onToggle}
        title={isExpanded ? "Minimize Preview" : "Expand Preview"}
        className={`
          /* Position & Shape */
          pointer-events-auto p-10 rounded-full
          flex items-center justify-center
          transition-all duration-300 ease-out
          backdrop-blur-sm shadow-xl

          bg-brand-700/20 text-brand-50 border border-brand-600/30

          opacity-60 group-hover:opacity-100
          scale-100 group-hover:scale-110
          hover:bg-brand-700 hover:text-white
        `}
      >
        {isExpanded
          ? <Minimize2 size={48} strokeWidth={1.5} />
          : <Maximize2 size={48} strokeWidth={1.5} />
        }
      </button>
    </div>
  );
}

/**
 * Scrollable PDF preview panel with Centered Expand/Minimize interaction.
 */
export function PDFPreview({ src, scrollTarget, defaultPageCount = 2 }) {
  const containerRef      = useRef(null);
  const pageRefs          = useRef({});        
  const scrollTimerRef    = useRef(null);
  const scrollTargetRef   = useRef(scrollTarget);
  const isFirstLoad       = useRef(true);
  const savedScrollTopRef = useRef(0);
  
  const [numPages, setNumPages]             = useState(null);
  const [containerWidth, setContainerWidth] = useState(480);
  const [pdfVisible, setPdfVisible]         = useState(true);
  
  // State for "Full Screen" mode
  const [isExpanded, setIsExpanded]         = useState(false);

  useEffect(() => { scrollTargetRef.current = scrollTarget; }, [scrollTarget]);

  useEffect(() => {
    if (!isFirstLoad.current && containerRef.current) {
      savedScrollTopRef.current = containerRef.current.scrollTop;
      setPdfVisible(false);
    }
  }, [src]);

  // ResizeObserver handles the width math for us automatically 
  // when we toggle the "fixed" wrapper class below.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      // Adjusted padding for better look when expanded
      const padding = isExpanded ? 64 : 8;
      setContainerWidth(Math.floor(entry.contentRect.width) - padding);
    });
    ro.observe(el);
    setContainerWidth(Math.floor(el.getBoundingClientRect().width) - (isExpanded ? 64 : 8));
    return () => ro.disconnect();
  }, [isExpanded]);

  const doScroll = useCallback(() => {
    const target = scrollTargetRef.current;
    if (!target || !containerRef.current) return;
    const { page, topFraction } = target;
    const pageEl = pageRefs.current[page];
    if (!pageEl) return;
    const containerEl  = containerRef.current;
    const pageRect     = pageEl.getBoundingClientRect();
    const containerRect = containerEl.getBoundingClientRect();
    const newScrollTop =
      containerEl.scrollTop +
      (pageRect.top - containerRect.top) +
      topFraction * pageEl.offsetHeight -
      20;
    containerEl.scrollTo({ top: Math.max(0, newScrollTop), behavior: "smooth" });
  }, []);

  useEffect(() => {
    if (!src) return;
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(doScroll, 80);
    return () => clearTimeout(scrollTimerRef.current);
  }, [src, scrollTarget, doScroll]);

  // Restore scroll position after expand/collapse layout settles
  useEffect(() => {
    if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    scrollTimerRef.current = setTimeout(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = savedScrollTopRef.current;
      }
    }, 80);
  }, [isExpanded]);

  const toggleExpand = (e) => {
    e.stopPropagation();
    if (containerRef.current) {
      savedScrollTopRef.current = containerRef.current.scrollTop;
    }
    setIsExpanded(prev => !prev);
  };

  return (
    /* New Outer Wrapper to handle 'fixed' vs 'relative' and hold the button context */
    <div className={`
      transition-all duration-300
      ${isExpanded 
        ? "fixed inset-0 z-50 w-screen h-screen" 
        : "relative w-full h-full"
      }
    `}>
      {/* Large Centered Green Button */}
      <ExpansionButton src={src} isExpanded={isExpanded} onToggle={toggleExpand} />

      {/* The original scrollable container */}
      <div
        ref={containerRef}
        className="overflow-y-auto w-full h-full bg-muted flex flex-col items-center py-5 px-4 gap-4"
      >
        {src ? (
          <div
            style={{
              opacity: pdfVisible ? 1 : 0,
              transition: pdfVisible ? "opacity 800ms ease-in" : "none",
              display: "contents",
            }}
          >
            <Document
              file={src}
              onLoadSuccess={({ numPages: n }) => {
                setNumPages(n);
                if (isFirstLoad.current) {
                  isFirstLoad.current = false;
                  if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
                  scrollTimerRef.current = setTimeout(doScroll, 300);
                } else {
                  if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
                  scrollTimerRef.current = setTimeout(() => {
                    if (containerRef.current) {
                      containerRef.current.scrollTop = savedScrollTopRef.current;
                    }
                    setPdfVisible(true);
                  }, 50);
                }
              }}
            >
              {Array.from({ length: numPages ?? defaultPageCount }, (_, i) => (
                <div
                  key={i + 1}
                  ref={(el) => { pageRefs.current[i + 1] = el; }}
                  className={`shadow-lg rounded overflow-hidden transition-all duration-300 ${isExpanded ? 'mb-8' : 'mb-0'}`}
                >
                  <PDFPage
                    pageNumber={i + 1}
                    width={isExpanded ? Math.min(containerWidth, 1100) : (containerWidth || 480)}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                  />
                </div>
              ))}
            </Document>
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-muted-foreground gap-3">
            <LucideFilePlus size={40} strokeWidth={1.5} />
            <p className="text-sm">Click &ldquo;Generate PDF&rdquo; to preview</p>
          </div>
        )}
      </div>
    </div>
  );
}